import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';

import { colorIdentidad, ORDEN_ENTIDADES } from '../config/rayos';
import { useModo } from '../config/modo';
import { useTranslation } from 'react-i18next';
import Emblema from '../components/Emblema';
import { CANALIZADORES, DESPERTARON, CANTANTES } from '../config/canalizadores';
import './gracias.css';

/**
 * GRACIAS.
 *
 * Faina la pidio expresamente (26-ago). No es una lista de creditos: es la
 * declaracion de que nada de esto se hizo solo, y es la pagina que separa a
 * esta fundacion de cualquier proyecto parecido.
 *
 * Cuatro agradecimientos, en este orden: a quienes hablaron · a quienes
 * escucharon antes · a quienes sostienen · a quien lee.
 *
 * 🔴 EL SEGUNDO ES EL DELICADO. Los 249 canalizadores del corpus NO estan
 * nombrados, y es a proposito: NOMBRAR A ALGUIEN NO AUTORIZA A PUBLICAR SU
 * TEXTO, y publicar sin permiso no se arregla con una mencion. Son dos
 * decisiones distintas y las dos siguen abiertas. Mientras no haya permisos se
 * agradece AL OFICIO, sin nombrar — y sigue siendo verdadero. El dia que
 * lleguen los permisos, cada nombre entra aqui con enlace a su obra.
 *
 * Las cifras estan contadas en el corpus, no estimadas: 5.778 canalizaciones,
 * 249 canalizadores, 4,3 millones de palabras.
 *
 * ⚠️ LA FRASE DEL USO TIENE QUE SER EXACTA. Una version anterior decia «no lo
 * citamos ni lo publicamos: lo estudiamos», y eso era FALSO: la regla maestra
 * de co-autoria del proyecto dice que las voces autorizadas NO confirman, sino
 * que ENTRAN — sus frases se incluyen casi textuales cuando son mas potentes
 * que las nuestras. Decirlo al reves seria una afirmacion publica inexacta
 * sobre el uso de obra ajena, justo en la pagina que habla de permisos.
 */

/**
 * Contadas en el corpus, no estimadas. Los miles se escriben ya con el punto
 * porque toLocaleString no separa en todos los navegadores.
 */

const GraciasPage = () => {
  const { t } = useTranslation();
  const { modo } = useModo();

  /** Todas las voces: se agradece a quien habló, aunque su pieza no esté escrita. */
  const voces = ORDEN_ENTIDADES;

  return (
    <Box className="gr">
      <Container maxWidth="md">

        {/* SOLO TÍTULOS Y NOMBRES (Faina, 26-ago: «borra todas las frases
            distintas de los nombres y títulos»). Los párrafos que explicaban
            cada bloque se fueron enteros: en una página de agradecimientos, el
            agradecimiento son los nombres. Explicar al lado por qué se agradece
            convierte el homenaje en un texto sobre nosotros.

            La única frase que queda es la de «A ti», que Faina mandó al
            principio: hace de entrada de la página y le habla al lector antes
            que a nadie. */}

        <Box className="gr-cabecera">
          <Typography variant="h1" className="gr-h1">{t('gracias.titulo')}</Typography>
        </Box>

        {/* ① A TI — al principio (Faina, 26-ago), a la izquierda como los otros
            cuatro y con una sola linea (28-ago).

            ⭐ LA FRASE ES DE FAINA, PALABRA POR PALABRA. Se probo una version
            larga de tres lineas, tejida con verbatim de las piezas de
            Trabajadores de la Luz, y la cambio por esta: dice lo mismo —la
            mision, el proposito y a quien se agradece— en catorce palabras.

            Se lee seguida del rotulo: «A ti · por traer tu luz al mundo». Por
            eso las dos lineas empiezan por «Por» y no llevan verbo propio: el
            rotulo no es un titulo, es la primera mitad de la oracion. Si algun
            dia se cambia el rotulo, hay que releer las tres juntas.

            SON DOS IDEAS Y SE PARTEN EN DOS (Faina, 28-ago): lo que ya das —tu
            luz— y lo que buscas —otro entendimiento—. Iban unidas por una «y»
            que las hacia pasar por una sola cosa; separadas y repitiendo el
            «Por», el paralelismo se ve y cada una se lee entera. Las palabras
            son suyas: solo se corto la «y» y se repitio el «Por».

            La letra es la cursiva de Literata, que ya viaja en el sitio: no
            anade descarga ninguna y es lo mas parecido a una dedicatoria
            escrita a mano que tenemos. */}
        <Box className="gr-bloque gr-bloque-ati">
          <Typography className="gr-rotulo">{t('gracias.a_ti')}</Typography>
          <Typography className="gr-cierre gr-ati-frase">
            {/* CADA FRASE CON SUS COMILLAS (Faina, 28-ago): son dos aportes
                distintos, y unas comillas que abarcaran las dos los harian
                pasar por una sola cita. */}
            <span className="gr-ati-linea">{t('gracias.a_ti_1')}</span>
            <span className="gr-ati-linea">
              {t('gracias.a_ti_2')}
            </span>
          </Typography>
        </Box>

        {/* ② */}
        <Box className="gr-bloque">
          <Typography className="gr-rotulo">{t('gracias.hablaron')}</Typography>
          <Box className="gr-voces">
            {voces.map((entidad) => (
              <RouterLink
                key={entidad}
                to={`/canalizaciones?voz=${encodeURIComponent(entidad)}`}
                className="gr-voz"
                style={{ '--rayo': colorIdentidad(entidad, modo) }}
              >
                <Emblema entidad={entidad} tamano={46} modo={modo} conHalo={false} />
                <span>{entidad}</span>
              </RouterLink>
            ))}
          </Box>
        </Box>

        {/* ③ Los 250 del corpus. Alfabéticos, justificados y sin el número de
            piezas de cada uno: un agradecimiento no es un ranking. Faina no está
            —lo pidió él mismo—: esta página agradece a los demás. */}
        <Box className="gr-bloque">
          <Typography className="gr-rotulo">{t('gracias.escucharon')}</Typography>
          <Box className="gr-nombres">
            {CANALIZADORES.map((n) => (
              <React.Fragment key={n}>
                <span className="gr-nombre">{n}</span>{' '}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* ④ LOS CANTANTES — de la hoja «Cantantes» de la misma tabla.
            No aparecian en el sitio pese a que la musica entra en los planes 2
            y 3. Van despues de los canalizadores porque su trabajo acompaña al
            texto, no lo trae.
            El rotulo es de Faina (27-ago, corregido por el mismo el 28-ago:
            «a quienes nos conectan con los que hablaron»). Rompe un poco el
            ritmo de los otros cuatro, mas cortos, pero dice lo que hace la
            musica aqui: no acompaña de fondo, abre la puerta.
            Y el cambio afina a quien se refiere: «las voces» es como el sitio
            llama a las catorce entidades, y no es eso — los cantantes conectan
            con QUIENES HABLARON, que es el rotulo del bloque de mas arriba.
            ✅ Al dia desde el 28-ago: los cantantes salen de las tres listas de
            Spotify de la Fundacion (MoM, Mantras y Meditations), via la tabla
            (297 el 28-ago, 280 el 9-sep: la lista se mueve sola, se regenera
            con herramientas/nombres.py tras _tabla_musica.py --actualizar). */}
        <Box className="gr-bloque">
          <Typography className="gr-rotulo">{t('gracias.conectan')}</Typography>
          <Box className="gr-nombres">
            {CANTANTES.map((n) => (
              <React.Fragment key={n}>
                <span className="gr-nombre">{n}</span>{' '}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* ⑤ Los ocho de la hoja «Otros» de la TABLA DE TRADUCCIONES DE
            CANALIZADORES Y CANALIZADOS, que Faina señaló como fuente. Son los
            libros y los maestros que abrieron esto antes de que hubiera
            proyecto, y por eso cierran: la pagina acaba apoyada en su origen.

            MISMO FORMATO QUE LOS TRES ANTERIORES (Faina, 28-ago). Antes iba en
            una caja con fondo y borde propios, y los nombres mas grandes; el
            26-ago pidio diferenciarlo y ahora pidio lo contrario. Es mejor asi:
            lo que distingue a estos ocho es el rotulo, no el envase, y una caja
            aparte al final rompia la unica cosa que sostiene esta pagina —una
            lista detras de otra, todas iguales, sin jerarquia entre quienes se
            agradece—. */}
        <Box className="gr-bloque">
          <Typography className="gr-rotulo">{t('gracias.despertaron')}</Typography>
          <Box className="gr-nombres">
            {DESPERTARON.map((n) => (
              <React.Fragment key={n}>
                <span className="gr-nombre">{n}</span>{' '}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* EL CIERRE (Faina, 28-ago). Decia «Puede haber un mensaje para ti» y
            era un enlace con flecha: la invitacion que usa el resto del sitio.
            Aqui sobraba —ofrecer producto al final de unos agradecimientos los
            convierte en un embudo— y Faina lo cambio dos veces: primero el
            texto, despues la forma. «Ponlo como una afirmacion y fuera la
            flecha».

            YA NO ES UN ENLACE. Sin flecha pero enlazado seguiria leyendose como
            un boton, y una afirmacion no se pulsa: se lee. Cierra el hilo que
            abre el bloque de arriba —ellos nos despertaron a nosotros— y se lo
            pasa a quien lee. Va en la cursiva con la que abre «A ti», para que
            la pagina empiece y termine con la misma letra.

            ⭐⭐ VA ENTRE COMILLAS, COMO LAS DOS DE «A ti», Y SIN FIRMA
            (Faina, 28-ago). Claude argumento primero que no llevara comillas
            —entrecomillar una frase propia la disfrazaria de canalizacion— y
            despues pidio una firma para saber de quien era. Las dos objeciones
            se caen con la regla que dio Faina:

              «TODO LO QUE ESTA ENTRE COMILLAS ES UNA CANALIZACION MIA (FAINA)
               O DE CUALQUIER ENTIDAD.»

            Es decir: en este sitio la comilla NO es un adorno tipografico, es
            un MARCADOR RESERVADO que significa «esto es canalizado». Y como
            Faina es canalizador del proyecto (Def 60), lo suyo tambien lo es.
            Por eso no hace falta firma: el lector no necesita saber de quien,
            le basta con saber que no es texto de redaccion.

            🔴 CONSECUENCIA PARA TODO EL SITIO: no entrecomillar nunca texto
            explicativo, de producto o de marketing. Las comillas estan
            ocupadas. Si hace falta destacar algo que no es canalizado, se hace
            con cursiva, tamaño o color — nunca con comillas.

            Lo medido en el corpus, por si algun dia se quiere el verbatim:
            «Tú también vas a despertar» no coincide con ningun cuerpo; lo mas
            cercano es «Dios ha prometido que despertarás» (9856JE2412JS), y ese
            cuerpo viene con la doctrina de la ilusion que el proyecto descarta.
            (El canalizador no se nombra aqui: la prueba de permisos no
            distingue un comentario del texto visible, y hace bien.)

            (Faina lo escribio «Tu»; la tilde y el punto son fix tecnico.) */}
        <Box className="gr-bloque gr-final">
          <Typography className="gr-afirmacion">{t('gracias.afirmacion')}</Typography>
        </Box>

      </Container>
    </Box>
  );
};

export default GraciasPage;
